import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

const STORAGE_KEY = 'demoView';

const DemoViewContext = createContext({
  demoView: true,
  setDemoView: () => {},
  toggleDemoView: () => {},
});

// Demo view is the DEFAULT for super admins — they land in the teacher-facing
// chrome and opt into super-admin view explicitly. An absent key therefore
// reads as `true`; only an explicit stored 'false' (chose super-admin view
// this session) turns it off.
const readInitial = () => {
  if (typeof window === 'undefined') return true;
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    return stored === null ? true : stored === 'true';
  } catch {
    return true;
  }
};

export function DemoViewProvider({ children }) {
  const [demoView, setDemoViewState] = useState(readInitial);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.sessionStorage.setItem(STORAGE_KEY, String(demoView));
    } catch {
      // sessionStorage unavailable (private mode, quota) — degrade silently;
      // state lives in memory for the tab lifetime, which is the same scope
      // sessionStorage would give us anyway.
    }
  }, [demoView]);

  const setDemoView = useCallback((next) => {
    setDemoViewState(Boolean(next));
  }, []);

  const toggleDemoView = useCallback(() => {
    setDemoViewState((prev) => !prev);
  }, []);

  const value = useMemo(
    () => ({ demoView, setDemoView, toggleDemoView }),
    [demoView, setDemoView, toggleDemoView],
  );

  return <DemoViewContext.Provider value={value}>{children}</DemoViewContext.Provider>;
}

DemoViewProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useDemoView() {
  return useContext(DemoViewContext);
}

export { DemoViewContext, STORAGE_KEY as DEMO_VIEW_STORAGE_KEY };
